<template>
  <div class="cpath">
    <ol class="cpath__list" :aria-label="label">
      <li
        v-for="(node, i) in nodes"
        :key="node.key"
        class="cpath__node"
        :class="`is-${node.state}`"
        :aria-current="node.state === 'current' ? 'step' : null"
      >
        <span class="cpath__pill">
          <v-icon small>{{ node.state === "done" ? "mdi-check" : node.icon }}</v-icon>
          <span>{{ node.text }}</span>
          <span v-if="node.state === 'current'" class="cpath__now">{{ node.key === "engineer" && working ? "în lucru" : "acum" }}</span>
        </span>
        <v-icon v-if="i < nodes.length - 1" small class="cpath__arrow" aria-hidden="true">mdi-arrow-right</v-icon>
      </li>
    </ol>
    <p v-if="review && showReviewNote" class="cpath__note">
      <v-icon small>mdi-magnify-scan</v-icon>Rectorul poate trimite demersul „Pentru examinare” la Șeful DTI.
    </p>
  </div>
</template>

<script>
import { ENGINEER_LABELS, stepIcon, stepText } from "@/config/circuits";

/**
 * The approvers of a circuit, then the engineer. With `waiting` (from the request details: 'sef', 'rector',
 * 'examinare', 'engineer', 'working', or null) the steps already passed are ticked and the current one stands out.
 */
export default {
  name: "CircuitPath",
  props: {
    steps: { type: Array, default: () => [] },
    review: { type: Boolean, default: false },
    engineerRole: { type: String, default: "" },
    // where the request is; undefined = just the circuit (Configurare)
    waiting: { type: String, default: undefined },
    // Îndeplinit: every step passed
    finished: { type: Boolean, default: false },
    // the rector sent it "Pentru examinare": the detour stays in the path once passed
    examined: { type: Boolean, default: false },
    showReviewNote: { type: Boolean, default: true },
    label: { type: String, default: "Traseul aprobării" },
  },
  computed: {
    working() {
      return this.waiting === "working";
    },
    nodes() {
      const list = [];
      (this.steps || []).forEach((key) => {
        list.push({ key, text: stepText(key), icon: stepIcon(key) });
        // the rector sent it "Pentru examinare": the detour, where the request is now or has been
        if (key === "rector" && (this.waiting === "examinare" || this.examined)) {
          list.push({ key: "examinare", text: stepText("examinare"), icon: stepIcon("examinare") });
        }
      });
      list.push({ key: "engineer", text: ENGINEER_LABELS[this.engineerRole] || "Inginer", icon: "mdi-account-wrench-outline" });

      const tracked = this.waiting !== undefined;
      const currentKey = this.working ? "engineer" : this.waiting;
      const at = list.findIndex((n) => n.key === currentKey);
      return list.map((node, i) => {
        let state = "plain";
        if (this.finished) state = "done";
        else if (tracked && at >= 0) state = i < at ? "done" : i === at ? "current" : "todo";
        return { ...node, state };
      });
    },
  },
};
</script>

<style scoped>
.cpath__list {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 0;
  margin: 0;
  padding: 0;
  list-style: none;
}
.cpath__node {
  display: inline-flex;
  align-items: center;
}
.cpath__pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 11px;
  border-radius: 999px;
  background: var(--sky-bg);
  color: var(--sky);
  font-size: 0.9rem;
  font-weight: 700;
  line-height: 1.5;
  white-space: nowrap;
}
.cpath__pill .v-icon {
  color: inherit !important;
}
.cpath__arrow {
  margin: 0 4px;
  color: var(--ink-3) !important;
}
.is-done .cpath__pill {
  background: #dcf1e4;
  color: var(--green-700);
}
.is-current .cpath__pill {
  background: var(--amber-bg);
  color: #6f4508;
  box-shadow: inset 0 0 0 2px var(--amber);
}
.is-todo .cpath__pill {
  background: var(--slate-bg);
  color: var(--slate);
}
.cpath__now {
  padding: 0 7px;
  border-radius: 999px;
  background: var(--amber);
  color: #fff;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.cpath__note {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 8px 0 0;
  color: var(--ink-2);
  font-size: 0.9rem;
}
.cpath__note .v-icon {
  color: var(--plum) !important;
}
</style>
